import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';

function cleanup(instance) {
  if (instance.element) {
    instance.element.removeEventListener('touchstart', instance.onTouchStart);
    instance.element.removeEventListener('touchmove', instance.onTouchMove);
    instance.element.removeEventListener('touchend', instance.onTouchEnd);
  }

  clearTimeout(instance.actionCompletedTimeout);
  clearTimeout(instance.resetTimeout);
}

export default class PullActionModifier extends Modifier {
  pullStartY = null;
  pullMoveY = null;
  dist = 0;
  distResisted = 0;
  ignoreTouches = false;
  status = 'ready';

  // Timeouts
  actionCompletedTimeout = null;
  resetTimeout = null;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  modify(element, _positional, named) {
    this.direction = named.direction || 'down'
    this.transform = named.transform || 'position'
    this.onTrigger = named.onTrigger;
    this.pullRegionHeight = named.pullRegionHeight || false
    this.distanceThreshold = named.distThreshold ?? 72;

    if (this.distanceThreshold > 0 && this.direction == 'up') {
      this.distanceThreshold = this.distanceThreshold * -1;
    }

    this.resistance = named.resistance ?? 2.5;
    this.actionDuration = named.actionDuration ?? 0;
    this.resetDuration = named.resetDuration ?? 200;
    this.disabled = named.disabled ?? false;

    if (!('ontouchstart' in window)) {
      return;
    }
    // Set element and options
    if (!this.element) {
      this.element = element;
      this.setupElement();
      this.setStatus();
    }
  }

  setupElement() {
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    this.element.addEventListener('touchstart', this.onTouchStart);
    this.element.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.element.addEventListener('touchend', this.onTouchEnd);
  }

  setStatus(status) {
    this.element.setAttribute('data-pull-direction', this.direction);
    this.status = status;
    if (!status) {
      this.element.removeAttribute('data-pull-state');
    } else {
      this.element.setAttribute('data-pull-state', status);
    }
  }

  clearTouchStatus() {
    this.pullStartY = null;
    this.pullMoveY = null;
    this.distance = 0;
    this.distanceResisted = 0;
    this.ignoreTouches = false;
  }

  updateReveal(height) {
    this.element.style.transform = `translateY(${height}px)`;

    if (this.transform === 'height') {
      let newHeight = this.originalHeight + (height * (this.direction == 'up' ? -1 : 1));
      this.element.style.height = `${newHeight}px`;
    }

  }

  resetReveal() {
    this.element.style.transition = `transform ${this.resetDuration}ms ease-in-out`;
    this.element.style.transform = 'translateY(0px)';
    this.element.style.height = `${this.originalHeight}px`;

    setTimeout(() => {
      this.element.style.transition = 'none';
    }, this.resetDuration);
  }

  reset(delay = 0) {
    this.resetTimeout = setTimeout(() => {
      this.clearTouchStatus();
      this.setStatus(false);
    }, delay);
  }

  triggerAction() {
    this.ignoreTouches = true;
    this.setStatus('triggering');
    const snapDuration = this.resetDuration * 0.5;
    const easing = 'ease-in-out';

    this.element.style.transition = `transform ${snapDuration}ms ${easing}`;
    this.updateReveal(this.distanceThreshold);

    setTimeout(() => {
      this.element.style.transition = 'none';
    }, snapDuration);

    if (this.onTrigger) {
      this.onTrigger();
    }

    this.actionCompletedTimeout = setTimeout(() => {
      this.setStatus('complete');
      this.resetReveal();
      this.reset(this.resetDuration);
    }, this.actionDuration);
  }

  isValidPull(e) {
    if (this.pullRegionHeight) {
      let touch = e.touches[0];
      let offsetY = touch.clientY - this.element.getBoundingClientRect().top;
      return (offsetY <= this.pullRegionHeight);
    } else {
      return true
    }
  }

  onTouchStart(e) {
    if (this.disabled || this.ignoreTouches) return;

    this.originalHeight = this.element.getBoundingClientRect().height;
    if (this.isValidPull(e)) {
      this.pullStartY = e.touches[0].screenY;
    } else {
      this.pullStartY = null;
    }
  }

  onTouchMove(e) {
    if (this.disabled || this.ignoreTouches || this.pullStartY === null) return;

    this.pullMoveY = e.touches[0].screenY;
    this.distance = this.pullMoveY - this.pullStartY;

    const isPullingDown = (this.direction === 'down' && this.distance > 0);
    const isPullingUp = (this.direction === 'up' && this.distance < 0);

    if (isPullingDown || isPullingUp) {
      e.preventDefault();

      // 1. Calculate the basic resisted distance
      let effectiveDistance = this.distance / this.resistance;

      // 2. Check if we have crossed the threshold
      // Down: distance > threshold | Up: distance < threshold (which is negative)
      const isPastThreshold = this.direction === 'down'
        ? effectiveDistance >= this.distanceThreshold
        : effectiveDistance <= this.distanceThreshold;

      if (!isPastThreshold) {
        this.distanceResisted = effectiveDistance;
        this.setStatus('pulling');
      } else {
        // 3. Apply extra resistance for "overpulling" beyond threshold
        const overpull = effectiveDistance - this.distanceThreshold;
        this.distanceResisted = this.distanceThreshold + (overpull * 0.3);
        this.setStatus('past-threshold');
      }

      this.updateReveal(this.distanceResisted);
    }
  }

  onTouchEnd() {
    if (this.disabled || this.ignoreTouches) return;

    // Check if we reached the required state
    if (this.status === 'past-threshold') {
        this.triggerAction();
    } else if (this.status === 'pulling') {
        this.ignoreTouches = true;
        this.setStatus('pull-cancelled');
        this.resetReveal();
        this.reset(this.resetDuration);
    } else {
      this.reset();
    }
  }
}
